import { FormEvent, useState } from 'react';
import applicationSettings from '../../infrastructure/config';
import {
  FieldFrame,
  PrimaryButton,
  RadioField,
  TertiaryButton,
  TextField,
} from '../../pages/common';
import styles from './AnswerFeedbackForm.module.css';

interface Props {
  onSubmit: (selectedOption: string, otherFeedback: string) => void;
  onClose: () => void;
}

const otherFeedbackLimit = 1000;

export const AnswerFeedbackForm = ({ onSubmit, onClose }: Props) => {
  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
  const [otherFeedback, setOtherFeedback] = useState<string>('');
  const [otherFeedbackError, setOtherFeedbackError] = useState<boolean>(false);

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (otherFeedbackError) {
      setOtherFeedbackError(false);
    }
    setSelectedOption(e.target.value);
  };

  const handleOtherFeedbackChange = (
    _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    if (!newValue || newValue.length <= otherFeedbackLimit) {
      if (otherFeedbackError) {
        setOtherFeedbackError(false);
      }
      setOtherFeedback(newValue ?? '');
    } else {
      setOtherFeedback(newValue.slice(0, otherFeedbackLimit));
      setOtherFeedbackError(true);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedOption) {
      return;
    }
    onSubmit(selectedOption, otherFeedback);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>Provide feedback</h2>
      {!!applicationSettings.feedbackConversationLoggingEnabled && (
        <p className={styles.disclaimer}>
          Conversation data, along with provided feedback, may be used to improve chatbot quality.
        </p>
      )}
      <form onSubmit={handleSubmit}>
        <RadioField
          name="feedback"
          value="incorrect"
          selectedValue={selectedOption}
          text="The response is incorrect or inaccurate"
          onChange={handleOptionChange}
        />
        <RadioField
          name="feedback"
          value="irrelevant"
          selectedValue={selectedOption}
          text="The response is irrelevant or off-topic"
          onChange={handleOptionChange}
        />
        <RadioField
          name="feedback"
          value="unclear"
          selectedValue={selectedOption}
          text="The response is unclear or confusing"
          onChange={handleOptionChange}
        />
        <RadioField
          name="feedback"
          value="incomplete"
          selectedValue={selectedOption}
          text="The response is incomplete or lacking detail"
          onChange={handleOptionChange}
        />
        <RadioField
          name="feedback"
          value="other"
          selectedValue={selectedOption}
          text="Other"
          onChange={handleOptionChange}
        />
        <div className={styles.controlLabel}>
          {selectedOption === 'other' && <span className={styles.required}>* </span>}
          Additional comments
        </div>
        <FieldFrame>
          <TextField
            className={styles.otherFeedbackField}
            placeholder="Provide additional details..."
            value={otherFeedback}
            onChange={handleOtherFeedbackChange}
          />
        </FieldFrame>
        <div className={styles.errorPanel}>
          {!!otherFeedbackError && <div>You can type up to 1000 characters.</div>}
        </div>
        <div className={styles.footer}>
          <TertiaryButton onClick={onClose} text="Cancel" type="button" />
          <PrimaryButton
            text="Submit"
            type="submit"
            disabled={!selectedOption || (selectedOption === 'other' && !otherFeedback.trim())}
          />
        </div>
      </form>
    </div>
  );
};
